import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useLang } from "../contexts/LangContext";
import { enableStickyWatcher } from "../utils/stickyWatcher";
import { useUserData } from "../contexts/UserContext";
import geoJson from "../data/bd-geo-units.json";
import { GeoData } from "../types/geo_units";
import { convertNumberInCurrentLang, convertWeight} from "../utils/misc";

const geoData = geoJson as GeoData;

type SortOrder = "consumption" | "logging";

interface LogEntry {
  personId: string;
  personName: string;
  personAge?: number;
  personSex?: string;
  personAddress: { en: string; bn: string };
  date: string;
  surveyedOn: string;
  latestMealLogTime: string;
  earliestMealLogTime: string;
  meals: Array<{ type: string; weight: number; weight_unit: string; fishNames: { en: string[]; bn: string[] } }>;
}

export default function AllLogs() {
  const perPage = 100;
  const MIN_LOGS_FOR_FILTERS = 5; // Configurable threshold
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { getSurveyedPersons, appData } = useUserData();
  const [sortOrder, setSortOrder] = useState<SortOrder>("logging");
  const [displayCount, setDisplayCount] = useState(perPage);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"desc" | "asc">("desc");

  // Helper to format datetime with UTC to local conversion
  const formatDateTime = (dateStr: string, includeTime: boolean = true) => {
    // Check if it's a datetime or just a date
    const hasTime = dateStr.includes('T') || dateStr.includes(' ');
    const date = new Date(dateStr); // Automatically converts from UTC to local
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const locale = lang === "bn" ? "bn-BD" : "en-US";
    
    // Check if it's today
    if (dateOnly.getTime() === today.getTime()) {
      const timeStr = hasTime && includeTime 
        ? ` ${date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true })}`
        : '';
      return `${t("today")}${timeStr}`;
    }
    
    // Check if it's yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dateOnly.getTime() === yesterday.getTime()) {
      const timeStr = hasTime && includeTime
        ? ` ${date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true })}`
        : '';
      return `${t("yesterday")}${timeStr}`;
    }
    
    // For other dates
    const dateFormatOptions: Intl.DateTimeFormatOptions = {
      month: lang === "en" ? "short" : "long",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    };
    
    if (hasTime && includeTime) {
      dateFormatOptions.hour = 'numeric';
      dateFormatOptions.minute = '2-digit';
      dateFormatOptions.hour12 = true;
    }
    
    return date.toLocaleString(locale, dateFormatOptions);
  };

  // construct both en and bn addresses
  const constructAddress = (address: any): { en: string; bn: string } => {
    if (!address) return { en: "", bn: "" };
    const parts_en: string[] = [];
    const parts_bn: string[] = [];
    
    // Village is stored as text, not ID
    if (address.village) {
       parts_en.push(address.village);
       parts_bn.push(address.village);
    }
    
    // Union, Upazila, District are stored as IDs - look up names
    if (address.union) {
      const allUpazilas = geoData.divisions.flatMap(div => div.districts.flatMap(d => d.upazilas));
      const upazila = allUpazilas.find(u => u.id === address.upazila);
      const union = upazila?.unions?.find(un => un.id === address.union);
      if (union) {       
        parts_en.push(union.name_en);
        parts_bn.push(union.name_bn);  
      }
    }
    
    if (address.upazila) {
      const allUpazilas = geoData.divisions.flatMap(div => div.districts.flatMap(d => d.upazilas));
      const upazila = allUpazilas.find(u => u.id === address.upazila);
      if (upazila) {
        parts_en.push(upazila.name_en);
        parts_bn.push(upazila.name_bn);
      }
    }
    
    if (address.district) {
      const allDistricts = geoData.divisions.flatMap(div => div.districts);
      const district = allDistricts.find(d => d.id === address.district);
      if (district) {
        parts_en.push(district.name_en);
        parts_bn.push(district.name_bn);
      }
    }
    
    return {
      en: parts_en.join(", "),
      bn: parts_bn.join(", ")
    }
  };

  // Collect all logs
  const allLogs = useMemo(() => {
    const persons = getSurveyedPersons() || [];
    const logs: LogEntry[] = [];

    for (const person of persons) {
      for (const dayRecord of person.records || []) {
        if (dayRecord.meals && dayRecord.meals.length > 0) {
          const mealsWithFish = dayRecord.meals.filter(
            (m) => m.fish_items && m.fish_items.length > 0
          );
          if (mealsWithFish.length > 0) {
            // Define meal order for sorting
            const mealOrder = { breakfast: 1, lunch: 2, snack: 3, dinner: 4 };
            
            // Sort meals by timing
            const sortedMeals = [...mealsWithFish].sort((a, b) => {
              const orderA = mealOrder[a.meal_type] || 5;
              const orderB = mealOrder[b.meal_type] || 5;
              return orderA - orderB;
            });

            // Get latest and earliest meal log times
            const mealTimes = mealsWithFish
              .map(m => m.updated_at || dayRecord.surveyed_on || dayRecord.date)
              .sort();
            const earliestMealLogTime = mealTimes[0];
            const latestMealLogTime = mealTimes[mealTimes.length - 1];

            logs.push({
              personId: person.id,
              personName: person.name || "Unknown",
              personAge: person.age,
              personSex: person.sex,
              personAddress: constructAddress(person.address),
              date: dayRecord.date,
              surveyedOn: dayRecord.surveyed_on || dayRecord.date,
              latestMealLogTime,
              earliestMealLogTime,
              meals: sortedMeals.map((meal) => {
                const totalWeight = meal.fish_items.reduce((sum, item) => {
                  const weight = convertWeight(item.estimated_weight || 0, item.weight_unit || "gm", "gm");
                  return sum + weight;
                }, 0);
                // Fishnames in both languages, and should not have duplicates in the lists
                const fishNames = {
                    'en': meal.fish_items.map(item => item.fish_english_name || item.fish_name).filter((v, i, a) => a.indexOf(v) === i),
                    'bn': meal.fish_items.map(item => item.fish_local_name || item.fish_name).filter((v, i, a) => a.indexOf(v) === i)
                };
                return {
                  type: meal.meal_type,
                  weight: totalWeight,
                  weight_unit: "gm",
                  fishNames,
                };
              }),
            });
          }
        }
      }
    }

    return logs;
  }, [appData?.persons]);

  // Sort logs based on selected order
  const sortedLogs = useMemo(() => {
    const logs = [...allLogs];
    if (sortOrder === "consumption") {
      // Sort by consumption date (date field)
      logs.sort((a, b) => {
        if (b.date !== a.date) {
          return sortDirection === "desc" 
            ? b.date.localeCompare(a.date)
            : a.date.localeCompare(b.date);
        }
        // Use latest meal log time for descending, earliest for ascending
        const timeA = sortDirection === "desc" ? a.latestMealLogTime : a.earliestMealLogTime;
        const timeB = sortDirection === "desc" ? b.latestMealLogTime : b.earliestMealLogTime;
        return sortDirection === "desc"
          ? timeB.localeCompare(timeA)
          : timeA.localeCompare(timeB);
      });
    } else {
      // Sort by logging date - use latest meal log time for descending, earliest for ascending
      logs.sort((a, b) => {
        const timeA = sortDirection === "desc" ? a.latestMealLogTime : a.earliestMealLogTime;
        const timeB = sortDirection === "desc" ? b.latestMealLogTime : b.earliestMealLogTime;
        if (timeB !== timeA) {
          return sortDirection === "desc"
            ? timeB.localeCompare(timeA)
            : timeA.localeCompare(timeB);
        }
        return sortDirection === "desc"
          ? b.date.localeCompare(a.date)
          : a.date.localeCompare(b.date);
      });
    }
    return logs;
  }, [allLogs, sortOrder, sortDirection]);

  // Filter logs by date range
  const filteredLogs = useMemo(() => {
    if (!fromDate && !toDate) return sortedLogs;
    
    return sortedLogs.filter(log => {
      const logDate = sortOrder === "consumption" ? log.date : log.surveyedOn;
      // Extract just the date part for comparison (YYYY-MM-DD)
      const logDateOnly = logDate.split('T')[0];
      if (fromDate && logDateOnly < fromDate) return false;
      if (toDate && logDateOnly > toDate) return false;
      return true;
    });
  }, [sortedLogs, fromDate, toDate, sortOrder]);

  // Set default date range when logs change
  useEffect(() => {
    if (allLogs.length > 0) {
      // Get earliest date based on sort type (use allLogs to find actual earliest, not sortedLogs)
      const dates = allLogs.map(log => {
        const dateValue = sortOrder === "consumption" ? log.date : log.surveyedOn;
        // Extract just the date part (YYYY-MM-DD) from potential ISO timestamp
        return dateValue.split('T')[0];
      });
      
      const earliestDate = dates.reduce((min, date) => date < min ? date : min, dates[0]);
      
      // Always set fromDate to earliest date when sort order or logs change
      setFromDate(earliestDate);
      
      // Set toDate to today if not already set
      if (!toDate) {
        const today = new Date().toISOString().split('T')[0];
        setToDate(today);
      }
    }
  }, [allLogs, sortOrder]);

  useEffect(() => {
    window.scrollTo(0, 0);
    enableStickyWatcher();
  }, []);

  useEffect(() => {
    document.title = "All Logs - Fish Tracker";
  }, []);

  // Reset display count when sort order changes
  useEffect(() => {
    setDisplayCount(perPage);
  }, [sortOrder, fromDate, toDate]);

  const loadMore = () => {
    setDisplayCount(prev => prev + perPage);
  };

  return (
    <div className="page relative flex flex-col grow pt-12 bg-transparent text-center">
      {/* Sticky header */}
      <div
        className="sticky-header sticky z-10 mb-4 px-scr py-4"
        data-watch-sticky
        data-sticky-top="auto"
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/survey')}
            className="p-1 rounded-full text-[#304050] hover:bg-white/50 transition-colors"
            aria-label={t("goBack")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="size-6" viewBox="0 0 24 24"><path fill="currentColor" d="M20 11.25a.75.75 0 0 1 0 1.5h-9.25V18a.75.75 0 0 1-1.28.53l-6-6a.75.75 0 0 1 0-1.06l6-6a.75.75 0 0 1 1.28.53v5.25z" /></svg>
          </button>

          <h1 className="text-3xl font-bold text-[#304050]">
            {t("allLogs_Title")}
          </h1>
          <div className="w-6"></div>
        </div>

        {/* Sort toggle */}
        <div className="mt-3 flex items-center justify-center gap-2">
          <button
            onClick={() => setSortOrder("consumption")}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              sortOrder === "consumption"
                ? "bg-blue-600 text-white"
                : "bg-slate-100 text-gray-700 hover:bg-slate-300"
            }`}
          >
            {t("allLogs_SortByConsumption")}
          </button>
          <button
            onClick={() => setSortOrder("logging")}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              sortOrder === "logging"
                ? "bg-blue-600 text-white"
                : "bg-slate-100 text-gray-700 hover:bg-slate-300"
            }`}
          >
            {t("allLogs_SortByLogging")}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-scr pb-12 pt-4">
        {sortedLogs.length === 0 ? (
          <div className="text-center mt-8 text-gray-600">
            <p>{t("allLogs_NoLogs")}</p>
          </div>
        ) : (
          <div className="space-y-2">

            <p className="text-gray-500 mb-4">
              {t("allLogs_TotalLogs")}: {convertNumberInCurrentLang(lang, filteredLogs.length)}
            </p>

            {/* Date filters - show only when logs > threshold */}
            {sortedLogs.length > MIN_LOGS_FOR_FILTERS && (
              <div className="flex items-center gap-2 mb-4">
                {/* From Date */}
                <div className="flex-1">
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full input-base input-small input-date !text-sm !px-2 !py-2"
                  />
                </div>
                
                {/* Hyphen separator */}
                <span className="text-gray-500 text-xl font-bold px-1">-</span>
                
                {/* To Date */}
                <div className="flex-1">
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full input-base input-small input-date !text-sm !px-2 !py-2"
                  />
                </div>
                
                {/* Sort Direction Toggle */}
                <button
                  onClick={() => setSortDirection(prev => prev === "desc" ? "asc" : "desc")}
                  className="p-2 bg-slate-200 bg-opacity-70 backdrop-blur-sm hover:bg-opacity-90 rounded-xl transition-all"
                  aria-label={sortDirection === "desc" ? "Sort ascending" : "Sort descending"}
                >
                  {sortDirection === "desc" ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
                    </svg>
                  )}
                </button>
              </div>
            )}

            {filteredLogs.slice(0, displayCount).map((log, idx) => (
              <button
                key={`${log.personId}-${log.date}-${idx}`}
                onClick={() =>
                  navigate(`/log-history/${log.personId}/${log.date}?from=all_logs`)
                }
                className="w-full text-left bg-slate-200 bg-opacity-25 backdrop-blur-sm p-3 rounded-xl hover:shadow-md bg-opacity-75 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center text-xl font-semibold text-rose-700 truncate">
                      <svg className="mr-2 text-gray-600" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="9" cy="9" r="2" /><path d="M13 15c0 1.105 0 2-4 2s-4-.895-4-2s1.79-2 4-2s4 .895 4 2Z" /><path d="M2 12c0-3.771 0-5.657 1.172-6.828S6.229 4 10 4h4c3.771 0 5.657 0 6.828 1.172S22 8.229 22 12s0 5.657-1.172 6.828S17.771 20 14 20h-4c-3.771 0-5.657 0-6.828-1.172S2 15.771 2 12Z" /><path strokeLinecap="round" d="M19 12h-4m4-3h-5m5 6h-3" /></g></svg>
                      <span 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/log-history/${log.personId}/?from=all_logs`);
                        }}
                        className="hover:underline cursor-pointer"
                      >
                        {log.personName}
                      </span>
                    </div>
                    {/* Person's info: age, sex, address in single line */}
                    <div className="text-sm text-blue-800 mt-1">
                      {log.personAge && <span>{ convertNumberInCurrentLang(lang, log.personAge)}{" "}{t('yearSymbol')}</span>}
                      {log.personAge && log.personSex && <span className="mx-1">•</span>}
                      {log.personSex && <span className="capitalize">{t(`${log.personSex}` as any)}</span>}
                      {(log.personAge || log.personSex) && log.personAddress && <span className="mx-1">•</span>}
                      {log.personAddress && <span>{lang === "bn" ? log.personAddress.bn : log.personAddress.en}</span>}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <div>
                        <span className="font-medium">{t("allLogs_Consumed")}:</span>{" "}
                        {formatDateTime(log.date, false)}
                      </div>
                      {log.surveyedOn !== log.date && (
                        <div>
                          <span className="font-medium">{t("allLogs_Logged")}:</span>{" "}
                          {formatDateTime(log.surveyedOn, true)}
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 mt-1 flex flex-wrap gap-1">
                      {log.meals.map((meal, i) => {
                        let fishDisplay = '';
                        const fishList = meal.fishNames[lang] || meal.fishNames['en'];
                        if (fishList.length === 1) {
                          fishDisplay = fishList[0];
                        } else if (fishList.length === 2) {
                          fishDisplay = fishList.join(` ${t("and")} `);
                        } else if (fishList.length > 2) {
                          fishDisplay = `${fishList[0]} ${t("and")} ${convertNumberInCurrentLang(lang, fishList.length - 1)} ${t("others")}`;
                        }
                        
                        return (
                          <span
                            key={i}
                            className="inline-block px-3 py-0.5 bg-slate-600 text-white rounded-full"
                          >
                            <span className="capitalize">{t(`mealType_${meal.type}` as any)}</span> • {convertNumberInCurrentLang(lang, Math.round(meal.weight))} {t(`unit_${meal.weight_unit}` as any)} ({fishDisplay})
                          </span>                          
                        );
                      })}
                    </div>
                  </div>
                  <div className="ml-2 text-gray-400">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                      />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
            
            {/* Load More button */}
            {filteredLogs.length > displayCount && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={loadMore}
                  className="button-base button-bg-grad-1 !py-3 px-8"
                >
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="size-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
                    </svg>
                    <span>{t("loadMore")}</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
